import { AfterViewInit, Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { MapInfoWindow, GoogleMap, MapPolygon, MapMarker, MapRectangle } from '@angular/google-maps';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface SavedPolygon {
  id: string;
  label?: string;
  path: google.maps.LatLngLiteral[];
  country?: string;
  googleInfo?: any;
  isEditing?: boolean;
}

interface SelectedPlace {
  place_id: string;
  name: string;
  formatted_address: string;
  geometry?: {
    location?: google.maps.LatLng;
  };
}

interface Country {
  name: string;
  code: string;
  bounds: google.maps.LatLngBoundsLiteral;
  center: google.maps.LatLngLiteral;
  zoom: number;
}

interface LocationCheckResult {
  location: string;
  coordinates: google.maps.LatLngLiteral;
  isInsidePolygon: boolean;
  containingPolygon?: SavedPolygon;
  googleInfo?: any;
}

interface MapType {
  name: string;
  value: string;
  icon: string;
}

@Component({
  selector: 'app-test-map',
  standalone: true,
  imports: [GoogleMap, MapPolygon, MapMarker, MapRectangle, CommonModule, FormsModule, CardModule, ButtonModule, InputTextModule, MessageModule, SelectModule, ToggleButtonModule],
  templateUrl: './test-map.html',
  styleUrl: './test-map.scss',
})
export class TestMap implements AfterViewInit {
  @ViewChild('mapRef', { static: false }) mapRef!: GoogleMap;
  @ViewChild('placeInput', { static: false }) placeInput!: ElementRef<HTMLInputElement>;
  @ViewChild('regionInput', { static: false }) regionInput!: ElementRef<HTMLInputElement>;
  @ViewChild('locationInput', { static: false }) locationInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;

  zoom = 7;
  center: google.maps.LatLngLiteral = { lat: 7.8731, lng: 80.7718 };
  selectedMapType = 'roadmap';
  options: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    restriction: {
      latLngBounds: { north: 90, south: -90, west: -180, east: 180 },
      strictBounds: false
    },
    minZoom: 4,
    maxZoom: 18
  };

  private drawingManager?: google.maps.drawing.DrawingManager;
  private geocoder?: google.maps.Geocoder;
  private placesService?: google.maps.places.PlacesService;
  
  polygons: SavedPolygon[] = [];
  status = '';
  selectedPlace: SelectedPlace | null = null;
  selectedCountry: Country | null = null;
  locationCheckResult: LocationCheckResult | null = null;
  searchLocationMarker: google.maps.LatLngLiteral | null = null;
  showCountryBoundary = false;
  editMode = false;
  editAllPolygons = false;
  
  // Runtime editing overlays and listeners to keep polygon paths in sync and validated
  private editingOverlays: Map<string, google.maps.Polygon> = new Map();
  private editingOverlayListeners: Map<string, google.maps.MapsEventListener[]> = new Map();
  private lastValidPaths: Map<string, google.maps.LatLngLiteral[]> = new Map();
  
  // Map type options
  mapTypes: MapType[] = [
    { name: 'Roadmap', value: 'roadmap', icon: 'pi pi-map' },
    { name: 'Satellite', value: 'satellite', icon: 'pi pi-globe' },
    { name: 'Hybrid', value: 'hybrid', icon: 'pi pi-image' },
    { name: 'Terrain', value: 'terrain', icon: 'pi pi-mountain' }
  ];
  
  // Sample countries with bounds and zoom levels
  countries: Country[] = [
    {
      name: 'United States',
      code: 'US',
      bounds: { north: 71.5388, south: 18.7763, west: -178.1165, east: -66.9346 },
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4
    },
    {
      name: 'United Kingdom',
      code: 'UK',
      bounds: { north: 60.9502, south: 49.0725, west: -8.8081, east: 1.9214 },
      center: { lat: 55.3781, lng: -3.4360 },
      zoom: 6
    },
    {
      name: 'Germany',
      code: 'DE',
      bounds: { north: 55.1482, south: 47.1803, west: 5.7226, east: 15.1856 },
      center: { lat: 51.1657, lng: 10.4515 },
      zoom: 6
    },
    {
      name: 'France',
      code: 'FR',
      bounds: { north: 51.0890, south: 41.3718, west: -5.1427, east: 9.5596 },
      center: { lat: 46.2276, lng: 2.2137 },
      zoom: 6
    },
    {
      name: 'Australia',
      code: 'AU',
      bounds: { north: -10.0628, south: -43.6346, west: 112.9211, east: 153.6387 },
      center: { lat: -25.2744, lng: 133.7751 },
      zoom: 4
    },
    {
      name: 'Canada',
      code: 'CA',
      bounds: { north: 83.3362, south: 41.6766, west: -141.0028, east: -52.3234 },
      center: { lat: 56.1304, lng: -106.3468 },
      zoom: 4
    },
    {
      name: 'Sri Lanka',
      code: 'LK',
      bounds: { north: 9.9231, south: 5.8435, west: 79.5760, east: 82.8574 },
      center: { lat: 7.8731, lng: 80.7718 },
      zoom: 10
    }
  ];

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    // Wait for Google Maps API to be fully loaded
    this.waitForGoogleMaps();
  }

  private waitForGoogleMaps() {
    if (!(window as any).google || !(window as any).google.maps) {
      this.status = 'Waiting for Google Maps API to load...';
      setTimeout(() => this.waitForGoogleMaps(), 500);
      return;
    }

    if (!(window as any).google.maps.drawing) {
      this.status = 'Waiting for Google Maps Drawing library to load...';
      setTimeout(() => this.waitForGoogleMaps(), 500);
      return;
    }

    const map = this.mapRef?.googleMap!;
    if (!map) {
      this.status = 'Map not ready yet, retrying...';
      setTimeout(() => this.waitForGoogleMaps(), 500);
      return;
    }

    this.status = 'Initializing map components...';
    this.geocoder = new google.maps.Geocoder();
    this.placesService = new google.maps.places.PlacesService(map);
    this.setupDrawingManager(map);
    this.setupPlaceAutocomplete();
    this.setupRegionAutocomplete();
    this.setupLocationAutocomplete();
    this.status = 'Map ready! Use the drawing controls (polygon icon) above the map to draw territories.';
  }

  onMapTypeChange() {
    this.options = {
      ...this.options,
      mapTypeId: this.selectedMapType as google.maps.MapTypeId
    };
    this.status = `Map type changed to ${this.mapTypes.find(t => t.value === this.selectedMapType)?.name}`;
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
    if (!this.editMode) {
      // Disable editing for all polygons
      this.polygons.forEach(poly => poly.isEditing = false);
    }
    this.status = this.editMode ? 'Edit mode enabled. Click on polygons to edit them.' : 'Edit mode disabled.';
  }

  private getPathFromOverlay(overlay: google.maps.Polygon): google.maps.LatLngLiteral[] {
    return overlay
      .getPath()
      .getArray()
      .map(p => ({ lat: p.lat(), lng: p.lng() }));
  }

  private createEditingOverlay(polygon: SavedPolygon) {
    if (!this.mapRef?.googleMap) return;
    if (this.editingOverlays.has(polygon.id)) return;

    const overlay = new google.maps.Polygon({
      paths: polygon.path,
      editable: true,
      draggable: true,
      strokeColor: '#00FF00',
      strokeWeight: 3,
      fillColor: '#00FF00',
      fillOpacity: 0.15,
      zIndex: 1000,
    });
    overlay.setMap(this.mapRef.googleMap!);

    this.editingOverlays.set(polygon.id, overlay);
    const listeners: google.maps.MapsEventListener[] = [];
    this.editingOverlayListeners.set(polygon.id, listeners);

    // Track last valid path for quick revert on invalid edit
    this.lastValidPaths.set(polygon.id, [...polygon.path]);

    const handlePathChanged = () => {
      const newPath = this.getPathFromOverlay(overlay);
      const validation = this.validatePolygonEdit(polygon, newPath);
      if (!validation.isValid) {
        // revert to last valid path
        const revertPath = this.lastValidPaths.get(polygon.id)!;
        overlay.setPath(revertPath);
        this.status = `Validation Error: ${validation.message}`;
        return;
      }
      // accept new path and keep model in sync
      polygon.path = [...newPath];
      this.lastValidPaths.set(polygon.id, [...newPath]);
      this.status = `Editing ${polygon.label || polygon.id}: ${validation.message}`;
    };

    // Listen to vertex edits and drag events
    const path = overlay.getPath();
    listeners.push(path.addListener('set_at', handlePathChanged));
    listeners.push(path.addListener('insert_at', handlePathChanged));
    listeners.push(path.addListener('remove_at', handlePathChanged));
    listeners.push(overlay.addListener('dragend', handlePathChanged));
  }

  private destroyEditingOverlay(polygonId: string, saveFinalPath: boolean) {
    const overlay = this.editingOverlays.get(polygonId);
    if (!overlay) return;

    if (saveFinalPath) {
      // Ensure final path is validated and synced
      const finalPath = this.getPathFromOverlay(overlay);
      const polygon = this.polygons.find(p => p.id === polygonId);
      if (polygon) {
        const validation = this.validatePolygonEdit(polygon, finalPath);
        if (validation.isValid) {
          polygon.path = [...finalPath];
        } else {
          // Revert to last known valid path
          const revertPath = this.lastValidPaths.get(polygonId) || polygon.path;
          polygon.path = [...revertPath];
        }
      }
    }

    overlay.setMap(null as unknown as google.maps.Map);
    this.editingOverlays.delete(polygonId);

    const listeners = this.editingOverlayListeners.get(polygonId) || [];
    listeners.forEach(l => l.remove());
    this.editingOverlayListeners.delete(polygonId);
    this.lastValidPaths.delete(polygonId);
  }

  onCountryChange() {
    if (this.selectedCountry) {
      // Update map bounds and center
      this.center = this.selectedCountry.center;
      this.zoom = this.selectedCountry.zoom;
      
      // Update map restriction with some padding for better visibility
      const bounds = this.selectedCountry.bounds;
      const latPadding = (bounds.north - bounds.south) * 0.1;
      const lngPadding = (bounds.east - bounds.west) * 0.1;
      
      this.options = {
        ...this.options,
        restriction: {
          latLngBounds: {
            north: bounds.north + latPadding,
            south: bounds.south - latPadding,
            west: bounds.west - lngPadding,
            east: bounds.east + lngPadding
          },
          strictBounds: true
        }
      };
      
      // Clear existing polygons and results
      this.polygons = [];
      this.selectedPlace = null;
      this.locationCheckResult = null;
      this.searchLocationMarker = null;
      this.showCountryBoundary = true;
      this.editMode = false;
      
      this.status = `Map restricted to ${this.selectedCountry.name}. Drawing is now limited to this area. You can zoom out to see the full country boundary.`;
    }
  }

  showFullCountry() {
    if (this.selectedCountry) {
      this.mapRef.googleMap!.fitBounds(new google.maps.LatLngBounds(
        { lat: this.selectedCountry.bounds.south, lng: this.selectedCountry.bounds.west },
        { lat: this.selectedCountry.bounds.north, lng: this.selectedCountry.bounds.east }
      ));
      this.status = `Showing full ${this.selectedCountry.name} area.`;
    }
  }

  private setupDrawingManager(map: google.maps.Map) {
    // Wait for the drawing library to be fully loaded
    if (typeof google.maps.drawing === 'undefined') {
      console.warn('Google Maps Drawing library not loaded yet, retrying...');
      setTimeout(() => this.setupDrawingManager(map), 1000);
      return;
    }

    this.drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: ['polygon'] as google.maps.drawing.OverlayType[],
      },
      polygonOptions: { 
        editable: false, 
        draggable: false,
        fillColor: '#FF0000',
        fillOpacity: 0.3,
        strokeColor: '#FF0000',
        strokeWeight: 2
      }
    });

    this.drawingManager.setMap(map);

    google.maps.event.addListener(this.drawingManager, 'overlaycomplete', (event: any) => {
      this.ngZone.run(() => {
        const overlay = event.overlay;
        const type = event.type;
        
        if (type === 'polygon') {
          const path = (overlay as google.maps.Polygon).getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
          
          // Use the improved area availability check
          if (!this.isAreaAvailableForNewPolygon(path)) {
            this.status = 'Error: This area is not available for new polygons. It may overlap with existing polygons or be outside the allowed area.';
            overlay.setMap(null);
            return;
          }
          
          // Check if polygon is within selected country bounds
          if (this.selectedCountry && !this.isPolygonInCountry(path)) {
            this.status = 'Error: Polygon must be within the selected country boundaries.';
            overlay.setMap(null);
            return;
          }
          
          const newPolygon: SavedPolygon = {
            id: this.makeId(),
            path,
            country: this.selectedCountry?.name,
            googleInfo: null,
            isEditing: false
          };
          
          this.polygons.push(newPolygon);
          overlay.setMap(null);
          this.selectedPlace = null;
          this.status = 'Polygon drawn and saved. Use Edit mode to modify polygons.';
          
          // Extract Google information for the polygon
          this.extractPolygonInfo(newPolygon);
        }
      });
    });
  }

  // Setup polygon editing event listeners
  setupPolygonEditing(polygon: SavedPolygon) {
    // This method will be called when a polygon enters edit mode
    // We'll need to track the polygon's Google Maps overlay to listen for changes
    if (!this.mapRef?.googleMap) return;
    
    // Find the polygon overlay on the map and add event listeners
    // Note: This is a simplified approach - in a real implementation you'd track the actual overlay
    this.status = `Editing polygon: ${polygon.label || polygon.id}. Changes will be validated for overlaps.`;
  }

  // Validate polygon changes during editing
  validatePolygonChanges(polygon: SavedPolygon, newPath: google.maps.LatLngLiteral[]): boolean {
    // Check if the new path overlaps with other polygons (excluding the current one)
    for (const otherPolygon of this.polygons) {
      if (otherPolygon.id !== polygon.id && this.polygonsIntersect(newPath, otherPolygon.path)) {
        return false;
      }
    }
    
    // Check if the new path is within country bounds
    if (this.selectedCountry && !this.isPolygonInCountry(newPath)) {
      return false;
    }
    
    return true;
  }

  // Update polygon path and validate
  updatePolygonPath(polygon: SavedPolygon, newPath: google.maps.LatLngLiteral[]) {
    if (this.validatePolygonChanges(polygon, newPath)) {
      polygon.path = [...newPath];
      this.status = `Polygon ${polygon.label || polygon.id} updated successfully.`;
      
      // Re-extract information for the updated polygon
      this.extractPolygonInfo(polygon);
    } else {
      this.status = 'Error: Polygon changes would create overlaps or be outside country bounds. Changes reverted.';
      // In a real implementation, you'd revert the polygon to its previous state
    }
  }

  // Check if a new polygon can be drawn at a specific location
  canDrawPolygonAtLocation(path: google.maps.LatLngLiteral[]): { canDraw: boolean; reason?: string } {
    // Check if polygon overlaps with existing ones
    if (this.checkPolygonOverlap(path)) {
      return { canDraw: false, reason: 'Polygon overlaps with existing polygons' };
    }
    
    // Check if polygon is within selected country bounds
    if (this.selectedCountry && !this.isPolygonInCountry(path)) {
      return { canDraw: false, reason: 'Polygon must be within the selected country boundaries' };
    }
    
    return { canDraw: true };
  }

  // Get available areas for drawing new polygons
  getAvailableDrawingAreas(): google.maps.LatLngBoundsLiteral[] {
    if (!this.selectedCountry) return [];
    
    // This is a simplified approach - in a real implementation you'd calculate
    // the actual available areas by subtracting existing polygons from the country bounds
    const bounds = this.selectedCountry.bounds;
    
    // For now, return the country bounds as available area
    // In a more sophisticated implementation, you'd calculate the difference
    // between country bounds and existing polygon areas
    return [bounds];
  }

  // Real-time validation during polygon editing
  validatePolygonEdit(polygon: SavedPolygon, newPath: google.maps.LatLngLiteral[]): { isValid: boolean; message: string } {
    // Check if the new path would overlap with other polygons
    for (const otherPolygon of this.polygons) {
      if (otherPolygon.id !== polygon.id && this.polygonsIntersect(newPath, otherPolygon.path)) {
        return { 
          isValid: false, 
          message: `Would overlap with polygon: ${otherPolygon.label || otherPolygon.id}` 
        };
      }
    }
    
    // Check if the new path is within country bounds
    if (this.selectedCountry && !this.isPolygonInCountry(newPath)) {
      return { 
        isValid: false, 
        message: 'Would be outside country boundaries' 
      };
    }
    
    return { isValid: true, message: 'Valid polygon path' };
  }

  // Force update overlap detection after polygon changes
  refreshOverlapDetection() {
    // This method can be called after polygon edits to ensure overlap detection is current
    this.status = 'Overlap detection refreshed. All polygons validated.';
  }

  // Check if a specific area is available for new polygons
  isAreaAvailableForNewPolygon(path: google.maps.LatLngLiteral[]): boolean {
    // This method checks if an area is truly free for new polygons
    // It accounts for all current polygon positions (including edited ones)
    return !this.checkPolygonOverlap(path);
  }

  private checkPolygonOverlap(newPath: google.maps.LatLngLiteral[]): boolean {
    for (const existingPolygon of this.polygons) {
      if (this.polygonsIntersect(newPath, existingPolygon.path)) {
        return true;
      }
    }
    return false;
  }

  private polygonsIntersect(path1: google.maps.LatLngLiteral[], path2: google.maps.LatLngLiteral[]): boolean {
    // More robust overlap detection
    // Check if any point of one polygon is inside the other
    for (const point of path1) {
      if (this.isPointInPolygon(point, path2)) {
        return true;
      }
    }
    for (const point of path2) {
      if (this.isPointInPolygon(point, path1)) {
        return true;
      }
    }
    
    // Check if any edges intersect
    for (let i = 0; i < path1.length; i++) {
      const edge1Start = path1[i];
      const edge1End = path1[(i + 1) % path1.length];
      
      for (let j = 0; j < path2.length; j++) {
        const edge2Start = path2[j];
        const edge2End = path2[(j + 1) % path2.length];
        
        if (this.edgesIntersect(edge1Start, edge1End, edge2Start, edge2End)) {
          return true;
        }
      }
    }
    
    return false;
  }

  private edgesIntersect(
    edge1Start: google.maps.LatLngLiteral, 
    edge1End: google.maps.LatLngLiteral,
    edge2Start: google.maps.LatLngLiteral, 
    edge2End: google.maps.LatLngLiteral
  ): boolean {
    // Check if two line segments intersect
    const x1 = edge1Start.lng;
    const y1 = edge1Start.lat;
    const x2 = edge1End.lng;
    const y2 = edge1End.lat;
    const x3 = edge2Start.lng;
    const y3 = edge2Start.lat;
    const x4 = edge2End.lng;
    const y4 = edge2End.lat;
    
    const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denominator) < 1e-10) return false; // Parallel lines
    
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;
    
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  }

  private isPointInPolygon(point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      if (((polygon[i].lat > point.lat) !== (polygon[j].lat > point.lat)) &&
          (point.lng < (polygon[j].lng - polygon[i].lng) * (point.lat - polygon[i].lat) / (polygon[j].lat - polygon[i].lat) + polygon[i].lng)) {
        inside = !inside;
      }
    }
    return inside;
  }

  private isPolygonInCountry(path: google.maps.LatLngLiteral[]): boolean {
    if (!this.selectedCountry) return true;
    
    for (const point of path) {
      if (point.lat > this.selectedCountry.bounds.north || 
          point.lat < this.selectedCountry.bounds.south ||
          point.lng > this.selectedCountry.bounds.east ||
          point.lng < this.selectedCountry.bounds.west) {
        return false;
      }
    }
    return true;
  }

  private async extractPolygonInfo(polygon: SavedPolygon) {
    if (!this.placesService) return;
    
    const center = this.computePolygonCenter(polygon.path);
    
    // Get reverse geocoding information
    this.geocoder?.geocode({ location: center }, (results, status) => {
      if (status === 'OK' && results) {
        this.ngZone.run(() => {
          polygon.googleInfo = {
            address: results[0].formatted_address,
            components: results[0].address_components,
            center: center,
            area: this.calculatePolygonArea(polygon.path)
          };
        });
      }
    });
  }

  private calculatePolygonArea(path: google.maps.LatLngLiteral[]): number {
    if (path.length < 3) return 0;
    
    let area = 0;
    for (let i = 0; i < path.length; i++) {
      const j = (i + 1) % path.length;
      area += path[i].lng * path[j].lat;
      area -= path[j].lng * path[i].lat;
    }
    area = Math.abs(area) / 2;
    
    // Convert to square kilometers (approximate)
    const earthRadius = 6371; // km
    return area * (Math.PI / 180) * earthRadius * earthRadius;
  }

  private setupPlaceAutocomplete() {
    const el = this.placeInput?.nativeElement;
    if (!el) return;

    const autocomplete = new google.maps.places.Autocomplete(el, {
      fields: ['place_id', 'formatted_address', 'geometry', 'name'],
      types: ['establishment'],
    });

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = (autocomplete as any).getPlace() as google.maps.places.PlaceResult;
        if (!place.place_id) {
          this.status = 'Select a valid business from suggestions.';
          return;
        }
        
        if (this.placesService) {
          this.placesService.getDetails({
            placeId: place.place_id,
            fields: ['place_id', 'name', 'formatted_address', 'geometry'],
          }, (details: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
            this.ngZone.run(() => {
              if (status === google.maps.places.PlacesServiceStatus.OK && details) {
                if (details.geometry?.location) {
                  // Check if location is within country bounds
                  if (this.selectedCountry && !this.isLocationInCountry(details.geometry.location)) {
                    this.status = 'Selected location is outside the restricted country area. Please select a location within ' + this.selectedCountry.name + '.';
                    return;
                  }
                  
                  this.mapRef.googleMap!.panTo(details.geometry.location);
                  this.mapRef.googleMap!.setZoom(17);
                }
                this.selectedPlace = {
                  place_id: details.place_id!,
                  name: details.name!,
                  formatted_address: details.formatted_address!,
                  geometry: details.geometry,
                };
                this.status = `Selected business: ${details.name}`;
              } else {
                this.status = `Could not fetch place details: ${status}`;
              }
            });
          });
        }
      });
    });
  }

  private setupRegionAutocomplete() {
    const el = this.regionInput?.nativeElement;
    if (!el) return;

    const autocomplete = new google.maps.places.Autocomplete(el, {
      types: ['(regions)'],
      fields: ['place_id', 'geometry', 'name', 'formatted_address'],
    });

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = (autocomplete as any).getPlace() as google.maps.places.PlaceResult;
        if (!place || !place.geometry) {
          this.status = 'Select a valid region/city from suggestions.';
          return;
        }
        this.selectedPlace = null;
        this.createSimpleBoundary(place.geometry.viewport!, place.name || '');
      });
    });
  }

  private setupLocationAutocomplete() {
    const el = this.locationInput?.nativeElement;
    if (!el) return;

    const autocomplete = new google.maps.places.Autocomplete(el, {
      fields: ['place_id', 'formatted_address', 'geometry', 'name'],
    });

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = (autocomplete as any).getPlace() as google.maps.places.PlaceResult;
        if (!place || !place.geometry?.location) {
          this.status = 'Select a valid location from suggestions.';
          return;
        }
        
        this.checkLocationInPolygons(place);
      });
    });
  }

  private checkLocationInPolygons(place: google.maps.places.PlaceResult) {
    if (!place.geometry?.location) return;
    
    const location = place.geometry.location;
    const coordinates = { lat: location.lat(), lng: location.lng() };
    
    // Check if location is within country bounds
    if (this.selectedCountry && !this.isLocationInCountry(location)) {
      this.status = 'Selected location is outside the restricted country area. Please select a location within ' + this.selectedCountry.name + '.';
      return;
    }
    
    // Check if location is inside any polygon
    let isInside = false;
    let containingPolygon: SavedPolygon | undefined;
    
    for (const polygon of this.polygons) {
      if (this.isPointInPolygon(coordinates, polygon.path)) {
        isInside = true;
        containingPolygon = polygon;
        break;
      }
    }
    
    // Move map to location
    this.mapRef.googleMap!.panTo(coordinates);
    this.mapRef.googleMap!.setZoom(15);
    this.searchLocationMarker = coordinates;
    
    // Create result
    this.locationCheckResult = {
      location: place.formatted_address || place.name || 'Unknown location',
      coordinates,
      isInsidePolygon: isInside,
      containingPolygon,
      googleInfo: {
        placeId: place.place_id,
        name: place.name,
        address: place.formatted_address,
        types: place.types
      }
    };
    
    this.status = isInside 
      ? `Location is inside polygon: ${containingPolygon?.label || containingPolygon?.id}`
      : 'Location is not inside any drawn polygon.';
  }

  private isLocationInCountry(location: google.maps.LatLng): boolean {
    if (!this.selectedCountry) return true;
    
    return location.lat() <= this.selectedCountry.bounds.north &&
           location.lat() >= this.selectedCountry.bounds.south &&
           location.lng() <= this.selectedCountry.bounds.east &&
           location.lng() >= this.selectedCountry.bounds.west;
  }

  private createSimpleBoundary(bounds: google.maps.LatLngBounds, placeName: string) {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const path = [
      { lat: ne.lat(), lng: sw.lng() }, { lat: ne.lat(), lng: ne.lng() },
      { lat: sw.lat(), lng: ne.lng() }, { lat: sw.lat(), lng: sw.lng() }
    ];
    
    // Check if boundary overlaps with existing polygons
    if (this.checkPolygonOverlap(path)) {
      this.status = 'Error: Boundary overlaps with existing polygons.';
      return;
    }
    
    this.polygons.push({ 
      id: this.makeId(), 
      label: placeName, 
      path,
      country: this.selectedCountry?.name,
      isEditing: false
    });
    this.mapRef.googleMap!.fitBounds(bounds);
    this.status = `Created boundary for: ${placeName}`;
  }

  removePolygon(id: string) {
    // Clean up any editing overlay first
    this.destroyEditingOverlay(id, false);
    this.polygons = this.polygons.filter((p) => p.id !== id);
    if (this.polygons.length === 0) {
      this.selectedPlace = null;
    }
  }

  editPolygon(polygon: SavedPolygon) {
    if (!this.editMode) {
      this.status = 'Please enable edit mode first using the toggle button.';
      return;
    }

    if (this.editAllPolygons) {
      // Enable editing for all polygons
      this.polygons.forEach(p => {
        p.isEditing = true;
        this.createEditingOverlay(p);
      });
      this.status = 'Editing enabled for all polygons. Changes are strictly validated; overlaps are not allowed.';
    } else {
      // Enable editing for selected polygon only
      this.polygons.forEach(p => {
        p.isEditing = false;
        this.destroyEditingOverlay(p.id, true);
      });
      polygon.isEditing = true;
      this.createEditingOverlay(polygon);
      this.status = `Editing enabled for polygon: ${polygon.label || polygon.id}. Overlaps are not allowed.`;
    }
    
    // Setup editing event listeners for the polygon
    this.setupPolygonEditing(polygon);
    this.onPolygonClick(polygon);
  }

  // Handle polygon path changes during editing
  onPolygonPathChange(polygon: SavedPolygon, newPath: google.maps.LatLngLiteral[]) {
    if (!polygon.isEditing) return;
    
    const validation = this.validatePolygonEdit(polygon, newPath);
    if (validation.isValid) {
      // Update the polygon path
      polygon.path = [...newPath];
      this.status = `Polygon ${polygon.label || polygon.id} updated: ${validation.message}`;
      
      // Re-extract information for the updated polygon
      this.extractPolygonInfo(polygon);
    } else {
      // Show validation error but don't revert (let user fix it)
      this.status = `Validation Error: ${validation.message}`;
    }
  }

  // Stop editing and validate final polygon state
  stopEditingPolygon(polygon: SavedPolygon) {
    if (!polygon.isEditing) return;
    
    // Final validation before stopping edit mode
    const validation = this.validatePolygonEdit(polygon, polygon.path);
    if (validation.isValid) {
      polygon.isEditing = false;
      this.destroyEditingOverlay(polygon.id, true);
      this.status = `Editing stopped for polygon: ${polygon.label || polygon.id}. Final state is valid.`;
      
      // Refresh overlap detection to ensure it's current
      this.refreshOverlapDetection();
    } else {
      this.status = `Cannot stop editing: ${validation.message}. Please fix the polygon first.`;
    }
  }

  stopEditingAll() {
    this.polygons.forEach(p => {
      p.isEditing = false;
      this.destroyEditingOverlay(p.id, true);
    });
    this.status = 'Editing stopped for all polygons.';
  }

  hasEditingPolygons(): boolean {
    return this.polygons.some(p => p.isEditing);
  }

  exportPolygonsAsGeoJson() {
    const features = this.polygons.map((p) => ({
      type: 'Feature',
      properties: { 
        id: p.id, 
        label: p.label || null,
        country: p.country || null,
        googleInfo: p.googleInfo || null
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[...p.path.map((pt) => [pt.lng, pt.lat]), [p.path[0].lng, p.path[0].lat]]],
      },
    }));
    return { type: 'FeatureCollection', features };
  }

  async saveAll() {
    const geojson = this.exportPolygonsAsGeoJson();
    console.log('Polygons geojson', geojson);
    this.selectedPlace = null;
    this.status = 'Polygons exported to console.';
  }

  onPolygonClick(poly: SavedPolygon) {
    const center = this.computePolygonCenter(poly.path);
    this.mapRef.googleMap!.panTo(center);
    this.mapRef.googleMap!.setZoom(12);
    this.selectedPlace = null;
    this.status = `Polygon ${poly.id} selected (${poly.label || 'no label'})`;
  }

  getStatusSeverity(): string {
    if (this.status.toLowerCase().includes('error')) return 'error';
    if (this.status.toLowerCase().includes('warning')) return 'warn';
    if (this.status.toLowerCase().includes('success')) return 'success';
    return 'info';
  }

  private computePolygonCenter(path: google.maps.LatLngLiteral[]) {
    let lat = 0, lng = 0;
    path.forEach((p) => { lat += p.lat; lng += p.lng; });
    return { lat: lat / path.length, lng: lng / path.length };
  }

  private makeId() {
    return Math.random().toString(36).slice(2, 9);
  }

  clearSelectedPlace() {
    this.selectedPlace = null;
    this.status = 'Place selection cleared.';
  }

  clearLocationCheck() {
    this.locationCheckResult = null;
    this.searchLocationMarker = null;
    this.status = 'Location check cleared.';
  }

  clearSearchMarker() {
    this.searchLocationMarker = null;
  }
}
